import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";

// @material-ui/core components
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

// @material-ui/icons
import Icon from "@material-ui/core/Icon";

// FontAwesome icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";

const useStyles = makeStyles(() => ({
  ganttMaster: {
    overflowY: "none",
    overflowX: "auto",
    position: "relative",
    fontFamily: "Roboto",
  },
  sortIcon: {
    fontSize: "12px",
    marginLeft: "6px",
    color: "#fff",
  },
  activeSortIcon: {
    color: "#fff",
  },
  emptyState: {
    margin: "40px auto",
    textAlign: "center",
    color: "#666",
  },
  emptyStateIcon: {
    fontSize: 48,
    color: "#ddd",
  },
  emptyStateTitle: {
    marginTop: 8,
    color: "#aaa",
  },
  emptyStateSub: {
    color: "#bbb",
  },
  ganttFlex: {
    display: "flex",
  },
  ganttTable: {
    background: "#fff",
    zIndex: 3,
    borderRight: "1px solid #ddd",
    minWidth: 220,
    flex: "0 0 auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  ganttTableHeader: {
    position: "sticky",
    top: 0,
    zIndex: 5,
    fontWeight: "bold",
    cursor: "pointer",
    color: "#fff",
    backgroundColor: "#9e9e9e",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "1.5vmin",
  },
  ganttTableColumns: {
    paddingLeft: "4px",
    paddingRight: "4px",
    borderRight: "1px solid #ddd",
    textAlign: "left",
    color: "#fff",
    backgroundColor: "#9e9e9e",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "1.5vmin",
  },
  row: {
    borderBottom: "1px solid #f0f0f0",
    transition: "background-color 0.2s ease",
  },
  rowHovered: {
    backgroundColor: "#f5f5f5",
  },
  cellFirst: {
    paddingLeft: "4px",
    paddingRight: "4px",
    borderRight: "1px solid #ddd",
    cursor: "pointer",
    textAlign: "left",
    fontWeight: 400,
    fontSize: "1.5vmin",
    color: "#333",
  },
  cell: {
    paddingLeft: "4px",
    paddingRight: "4px",
    borderRight: "1px solid #ddd",
    textAlign: "center",
    fontWeight: 400,
    fontSize: "1.5vmin",
    color: "#333",
  },
  initiativeSpan: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 140,
    width: "100%",
    display: "block",
    cursor: "pointer",
  },
  timelineScroll: {
    flex: 1,
    overflowX: "auto",
  },
  timelineContainer: {
    position: "relative",
  },
  timelineGrid: {
    display: "grid",
  },
  periodHeader: {
    textAlign: "center",
    fontSize: "1.5vmin",
    fontWeight: "bold",
    color: "#fff",
    backgroundColor: "#555555",
    borderBottom: "1px solid #e0e0e0",
    borderRight: "1px solid #e0e0e0",
    padding: "2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  barRow: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    padding: "4px 0",
    animationName: "$slideIn",
    animationDuration: "0.5s",
    animationTimingFunction: "ease-out",
    animationFillMode: "backwards",
  },
  bar: {
    width: "100%",
    height: "100%",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    padding: "0 8px",
    transition: "all 0.2s ease-out",
  },
  captureMarker: {
    position: "absolute",
    top: "50%",
    transform: "translate(-50%, -50%) rotate(45deg)",
    width: 10,
    height: 10,
    backgroundColor: "#fff",
    zIndex: 1,
  },
  gridCell: {
    borderRight: "1px solid #f0f0f0",
    borderBottom: "1px solid #f0f0f0",
    zIndex: -1,
    transition: "background-color 0.2s ease",
  },
  todayMarker: {
    position: "absolute",
    top: 28,
    bottom: 0,
    width: 2,
    backgroundColor: "red",
    zIndex: 10,
    opacity: 0.7,
    pointerEvents: "none",
  },
  todayMarkerDot: {
    position: "absolute",
    top: -8,
    left: -3,
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "red",
  },
  "@keyframes slideIn": {
    "0%": { transform: "translateX(-20px)", opacity: 0 },
    "100%": { transform: "translateX(0)", opacity: 1 },
  },
}));

const ConsolidatedInitiativesGantt = ({
  initiatives,
  t,
  onSelect,
  viewMode,
}) => {
  const classes = useStyles();
  const { t: tHook } = useTranslation();
  // prefer provided t, fallback to hook
  const translate = t || tHook;

  /* ╭─ 1. Controls ───────────────────────────────────────────╮ */
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const GANTT_ROW_HEIGHT = 25;

  /* ╭─ 2. Order ───────────────────────────────╮ */
  const [sortCfg, setSortCfg] = useState({ key: "proj_start", dir: "asc" });
  const toggleSort = (k) =>
    setSortCfg((p) =>
      p.key === k
        ? { key: k, dir: p.dir === "asc" ? "desc" : "asc" }
        : { key: k, dir: "asc" },
    );
  const sortIcon = (k) =>
    sortCfg.key !== k ? (
      <FontAwesomeIcon icon={faSort} className={classes.sortIcon} />
    ) : (
      <FontAwesomeIcon
        icon={sortCfg.dir === "asc" ? faSortUp : faSortDown}
        className={`${classes.sortIcon} ${classes.activeSortIcon}`}
      />
    );

  const sorted = useMemo(() => {
    const arr = [...initiatives];
    arr.sort((a, b) => {
      let av = a[sortCfg.key],
        bv = b[sortCfg.key];
      if (["proj_start", "cap_start"].includes(sortCfg.key)) {
        av = av || "";
        bv = bv || "";
        return sortCfg.dir === "asc"
          ? av.localeCompare(bv)
          : bv.localeCompare(av);
      }
      if (typeof av === "string") {
        av = av.toLowerCase();
        bv = bv.toLowerCase();
      }
      if (av < bv) return sortCfg.dir === "asc" ? -1 : 1;
      if (av > bv) return sortCfg.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [initiatives, sortCfg]);

  const MIN_CELL_WIDTH = { month: 65, quarter: 70, year: 80 };

  /* ╭─ 3. Timeline ─────────────────────────╮ */
  const parseYmToUtcDate = (ymString) => {
    if (!ymString || !/^\d{4}-\d{2}$/.test(ymString)) return null;
    const [year, month] = ymString.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, 1));
  };

  const { timelineStart, timelineEnd } = useMemo(() => {
    if (!sorted.length) {
      const now = new Date();
      const year = now.getUTCFullYear();
      return {
        timelineStart: new Date(Date.UTC(year, 0, 1)),
        timelineEnd: new Date(Date.UTC(year, 11, 1)),
      };
    }
    let dates = sorted
      .flatMap((i) =>
        [i.proj_start, i.cap_start, i.cap_end].map(parseYmToUtcDate),
      )
      .filter(Boolean);
    if (dates.length === 0) {
      const now = new Date();
      const year = now.getUTCFullYear();
      return {
        timelineStart: new Date(Date.UTC(year, 0, 1)),
        timelineEnd: new Date(Date.UTC(year, 11, 1)),
      };
    }
    let min = new Date(Math.min(...dates));
    let max = new Date(Math.max(...dates));
    min.setUTCDate(1);
    if (viewMode === "quarter")
      min.setUTCMonth(Math.floor(min.getUTCMonth() / 3) * 3);
    if (viewMode === "year") min.setUTCMonth(0);
    return { timelineStart: min, timelineEnd: max };
  }, [sorted, viewMode]);

  /* ╭─ 4. Periods HEADER ────────────────────────────────────╮ */
  const periods = useMemo(() => {
    const out = [];
    const cur = new Date(timelineStart.getTime());
    if (viewMode === "month") {
      while (cur <= timelineEnd) {
        out.push({ y: cur.getUTCFullYear(), m: cur.getUTCMonth() });
        cur.setUTCMonth(cur.getUTCMonth() + 1);
      }
    } else if (viewMode === "quarter") {
      while (cur <= timelineEnd) {
        out.push({
          y: cur.getUTCFullYear(),
          q: Math.floor(cur.getUTCMonth() / 3) + 1,
        });
        cur.setUTCMonth(cur.getUTCMonth() + 3);
      }
    } else {
      while (cur <= timelineEnd) {
        out.push({ y: cur.getUTCFullYear() });
        cur.setUTCFullYear(cur.getUTCFullYear() + 1);
      }
    }
    return out;
  }, [timelineStart, timelineEnd, viewMode]);

  const diffPeriods = (d1, d2) => {
    if (!d1 || !d2) return 0;
    if (viewMode === "month")
      return (
        (d2.getUTCFullYear() - d1.getUTCFullYear()) * 12 +
        (d2.getUTCMonth() - d1.getUTCMonth())
      );
    if (viewMode === "quarter")
      return (
        (d2.getUTCFullYear() - d1.getUTCFullYear()) * 4 +
        Math.floor(d2.getUTCMonth() / 3) -
        Math.floor(d1.getUTCMonth() / 3)
      );
    return d2.getUTCFullYear() - d1.getUTCFullYear();
  };

  const getGridPos = (date) => {
    if (!(date instanceof Date)) return null;
    return diffPeriods(timelineStart, date) + 1;
  };

  // no-op

  const formatHumanDate = (dateString) => {
    if (!dateString) return "";
    const [year, month] = dateString.split("-");
    if (!year || !month) return dateString;
    const monthNumber = parseInt(month, 10);
    const monthName = translate(`months.${monthNumber}`);
    return `${monthName}-${year}`;
  };

  const todayMarkerPosition = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (today < timelineStart || today > timelineEnd) return null;
    const startPos = diffPeriods(timelineStart, today) + 1;
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    let percentage;
    if (viewMode === "month") {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      percentage = (day - 1) / daysInMonth;
    } else if (viewMode === "quarter") {
      const qStartMonth = Math.floor(month / 3) * 3;
      const qStart = new Date(year, qStartMonth, 1);
      const qEnd = new Date(year, qStartMonth + 3, 0);
      const totalDays = (qEnd - qStart) / (1000 * 60 * 60 * 24) + 1;
      const elapsedDays = (today - qStart) / (1000 * 60 * 60 * 24);
      percentage = elapsedDays / totalDays;
    } else {
      const yearStart = new Date(year, 0, 1);
      const nextYear = new Date(year + 1, 0, 1);
      const totalDays = (nextYear - yearStart) / (1000 * 60 * 60 * 24);
      const elapsedDays = (today - yearStart) / (1000 * 60 * 60 * 24);
      percentage = elapsedDays / totalDays;
    }
    return startPos + percentage - 1;
  }, [viewMode, timelineStart, timelineEnd]);

  return (
    <div className={classes.ganttMaster}>
      {sorted.length === 0 && (
        <div className={classes.emptyState}>
          <Icon className={classes.emptyStateIcon}>calendar_today</Icon>
          <Typography variant="h6" className={classes.emptyStateTitle}>
            {translate("initiatives.no_initiatives_for_filter")}
          </Typography>
          <Typography variant="body2" className={classes.emptyStateSub}>
            {translate("initiatives.try_adjusting_filters")}
          </Typography>
        </div>
      )}

      {sorted.length > 0 && (
        <div className={classes.ganttFlex}>
          <div className={classes.ganttTable}>
            <table className={classes.table}>
              <thead
                className={classes.ganttTableHeader}
                style={{ height: GANTT_ROW_HEIGHT }}
              >
                <tr>
                  {[
                    {
                      k: "initiative",
                      label: translate("initiatives.initiative"),
                    },
                    {
                      k: "proj_start",
                      label: translate("initiatives.project_start"),
                    },
                    {
                      k: "cap_start",
                      label: translate("initiatives.capture_start"),
                    },
                    {
                      k: "globalBenefit",
                      label: translate("initiatives.benefit_global"),
                    },
                  ].map((c) => (
                    <th
                      key={c.k}
                      onClick={() => toggleSort(c.k)}
                      className={classes.ganttTableColumns}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <span>{c.label}</span>
                        <span>{sortIcon(c.k)}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr
                    key={r.id}
                    className={`${classes.row} ${hoveredRowId === r.id ? classes.rowHovered : ""}`}
                    style={{ height: GANTT_ROW_HEIGHT }}
                    onMouseEnter={() => setHoveredRowId(r.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                  >
                    <td className={classes.cellFirst}>
                      <Tooltip title={r.initiative} arrow>
                        <span
                          className={classes.initiativeSpan}
                          onClick={() => onSelect && onSelect(r.id)}
                        >
                          {r.initiative}
                        </span>
                      </Tooltip>
                    </td>
                    <td className={classes.cell}>
                      {formatHumanDate(r.proj_start)}
                    </td>
                    <td className={classes.cell}>
                      {formatHumanDate(r.cap_start)}
                    </td>
                    <td className={classes.cell}>{r.globalBenefit}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={classes.timelineScroll}>
            <div
              className={classes.timelineContainer}
              style={{
                minWidth: `${periods.length * MIN_CELL_WIDTH[viewMode]}px`,
              }}
            >
              <div
                className={classes.timelineGrid}
                style={{
                  gridTemplateColumns: `repeat(${periods.length}, minmax(${MIN_CELL_WIDTH[viewMode]}px, 1fr))`,
                  gridTemplateRows: `${GANTT_ROW_HEIGHT}px repeat(${sorted.length}, ${GANTT_ROW_HEIGHT}px)`,
                }}
              >
                {periods.map((p, i) => (
                  <div
                    key={i}
                    className={classes.periodHeader}
                    style={{
                      gridColumn: `${i + 1} / span 1`,
                      gridRow: "1 / 1",
                    }}
                  >
                    {viewMode === "month" &&
                      `${translate(`months.${p.m + 1}`).substring(0, 3)}-${String(p.y).slice(-2)}`}
                    {viewMode === "quarter" && `Q${p.q} ${p.y}`}
                    {viewMode === "year" && p.y}
                  </div>
                ))}

                {sorted.map((r, rowIndex) => {
                  const hasDates = r.proj_start && r.cap_start && r.cap_end;
                  if (!hasDates) return null;
                  const colour =
                    {
                      red: "#E86C75",
                      yellow: "#FFCB6B",
                      green: "#6ECF93",
                      gray: "#B0B0B0",
                    }[r.status] || "#B0B0B0";
                  const isHovered = hoveredRowId === r.id;
                  const projStartPos = getGridPos(
                    parseYmToUtcDate(r.proj_start),
                  );
                  const capStartPos = getGridPos(parseYmToUtcDate(r.cap_start));
                  const capEndPos = getGridPos(parseYmToUtcDate(r.cap_end));
                  if (
                    projStartPos === null ||
                    capStartPos === null ||
                    capEndPos === null
                  )
                    return null;
                  const barEndGridLine = capEndPos + 1;
                  const totalDurationInPeriods = barEndGridLine - projStartPos;
                  const projectPhaseDurationInPeriods =
                    capStartPos - projStartPos;
                  const positionOfCaptureStartCenter =
                    projectPhaseDurationInPeriods + 0.5;
                  const captureStartPercentage =
                    totalDurationInPeriods > 0
                      ? (positionOfCaptureStartCenter /
                          totalDurationInPeriods) *
                        100
                      : 0;

                  return (
                    <div
                      key={r.id}
                      className={classes.barRow}
                      style={{
                        gridColumn: `${projStartPos} / ${capEndPos + 1}`,
                        gridRow: `${rowIndex + 2} / span 1`,
                        zIndex: isHovered ? 20 : 1,
                        animationDelay: `${rowIndex * 0.05}s`,
                      }}
                      onMouseEnter={() => setHoveredRowId(r.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                    >
                      <Tooltip
                        title={
                          <div style={{ textAlign: "left" }}>
                            <div>
                              <strong>{r.initiative}</strong>
                            </div>
                            <div>
                              <strong>
                                {translate("initiatives.project_start")}:
                              </strong>{" "}
                              {formatHumanDate(r.proj_start)}
                            </div>
                            <div>
                              <strong>
                                {translate("initiatives.capture")}:
                              </strong>{" "}
                              {formatHumanDate(r.cap_start)} →{" "}
                              {formatHumanDate(r.cap_end)}
                            </div>
                          </div>
                        }
                        arrow
                      >
                        <div
                          onClick={() => onSelect && onSelect(r.id)}
                          className={classes.bar}
                          style={{
                            filter: isHovered
                              ? "brightness(1.1)"
                              : "brightness(1)",
                            background: `linear-gradient(to right, ${colour}80 ${captureStartPercentage}%, ${colour} ${captureStartPercentage}%)`,
                          }}
                        >
                          <div
                            className={classes.captureMarker}
                            style={{
                              left: `${captureStartPercentage}%`,
                              border: `1px solid ${colour}`,
                            }}
                          />
                        </div>
                      </Tooltip>
                    </div>
                  );
                })}

                {sorted.map((r, rowIndex) =>
                  periods.map((__, periodIndex) => (
                    <div
                      key={`${r.id}-${periodIndex}`}
                      className={classes.gridCell}
                      style={{
                        gridColumn: `${periodIndex + 1} / span 1`,
                        gridRow: `${rowIndex + 2} / span 1`,
                        backgroundColor:
                          hoveredRowId === r.id ? "#f5f5f5" : "transparent",
                      }}
                      onMouseEnter={() => setHoveredRowId(r.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                    />
                  )),
                )}
              </div>
              {todayMarkerPosition && (
                <div
                  className={classes.todayMarker}
                  style={{
                    left: `calc((100% / ${periods.length}) * ${todayMarkerPosition})`,
                  }}
                >
                  <div className={classes.todayMarkerDot} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ConsolidatedInitiativesGantt.propTypes = {
  initiatives: PropTypes.array.isRequired,
  t: PropTypes.func, // now optional (fallback to hook)
  onSelect: PropTypes.func,
  viewMode: PropTypes.string,
  setViewMode: PropTypes.func,
};

export default ConsolidatedInitiativesGantt;
